import { StatementListResponseType } from "@sellar-io/api-v2/types";
import { createSellarColumnHelper } from "@sellar-io/components";

import { useMemo } from "react";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import { formatCurrency } from "@sellar-io/common";

import { useShallow } from "zustand/shallow";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useSellarCreditStatementStore } from "../../../../stores/SellarCreditStatementStore";
import { SellarCreditStatementSortType } from "../../../../stores/SellarCreditStatementTypes";
import { StatementPaymentStatusBadge } from "./StatementPaymentStatusBadge";
import { StatementDownloadButton } from "./StatementDownloadButton";
import {
  Icon,
  ProfilePicture,
  SpinnerLegacy,
} from "../../../../../../components";
import { TableInfinite } from "../../../../../../components/table/TableInfinite";

type ColumnDataType = StatementListResponseType[number];

export const SellarCreditStatementTable = ({
  query,
  handleTableSort,
}: {
  query: UseInfiniteQueryResult<InfiniteData<StatementListResponseType>, Error>;
  handleTableSort: (value: SellarCreditStatementSortType | null) => void;
}) => {
  const { columnSort } = useSellarCreditStatementStore(
    useShallow((state) => ({
      columnSort: state.columnSort,
    })),
  );

  const { data, fetchNextPage, isFetching } = query;
  const flatData = useMemo(
    () => data?.pages.flatMap((page) => page.map((row) => row)) ?? [],
    [data],
  );

  const getTotal = () => {
    return flatData.reduce((prev, invoice) => {
      if (invoice.type === "INV" && invoice.status !== "void") {
        return prev + invoice.total;
      }
      return prev;
    }, 0);
  };

  const columnHelper = createSellarColumnHelper<ColumnDataType>();

  const columns = [
    columnHelper.accessor("type", {
      id: "type",
      header: "Type",
      enableSorting: false,
      cell: (info) => {
        const type = info.getValue();
        return (
          <span
            className={twMerge(
              "flex items-center gap-1",
              type === "CN" && "text-emerald-600",
            )}
          >
            <Icon
              icon={
                type === "INV" ? "FILE_DOCUMENT_OUTLINE" : "NOTE_EDIT_OUTLINE"
              }
            />
            {type}
          </span>
        );
      },
    }),
    columnHelper.accessor("TwoOrder", {
      id: "twoOrder",
      header: "#",
      enableSorting: false,
      cell: (info) => {
        const twoOrder = info.getValue();
        return twoOrder?.twoInvoiceNumber;
      },
    }),
    columnHelper.accessor("Supplier", {
      id: "supplier",
      header: "Supplier",
      enableSorting: true,
      cell: (info) => {
        const supplier = info.getValue();
        return (
          <span className="flex items-center gap-2">
            <ProfilePicture
              name={supplier.name}
              logoUrl={supplier.logoUrl}
              size="small"
            />{" "}
            {supplier.name}
          </span>
        );
      },
    }),
    columnHelper.accessor("Business", {
      id: "business",
      header: "Business",
      enableSorting: true,
      cell: (info) => info.getValue().name,
    }),
    columnHelper.accessor("orderPlacedByName", {
      id: "user",
      header: "User",
      enableSorting: false,
      cell: (info) => info.getValue() ?? "-",
    }),
    columnHelper.accessor((row) => row, {
      id: "total",
      header: "Amount",
      enableSorting: false,
      cell: (info) => {
        const row = info.getValue();
        return (
          <span className={row.type === "CN" ? "text-emerald-600" : ""}>
            {row.type === "CN" && "(+) "}
            {formatCurrency(row.total)}
          </span>
        );
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "status",
      header: "Payment status",
      enableSorting: false,
      cell: (info) => {
        const statement = info.getValue();
        return statement.type === "CN" ? (
          "-"
        ) : (
          <StatementPaymentStatusBadge statement={statement} />
        );
      },
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: "Date created",
      enableSorting: true,
      cell: (info) => {
        const createdAt = info.getValue();
        return dayjs(createdAt).format("DD/MM/YYYY");
      },
    }),
    columnHelper.accessor((row) => row, {
      id: "action",
      header: "",
      enableSorting: false,
      cell: (info) => (
        <StatementDownloadButton
          document={info.getValue()}
          className="flex w-full justify-end"
        />
      ),
    }),
  ];

  if (query.isLoading && flatData.length === 0) {
    return <SpinnerLegacy />;
  }

  return (
    <div className={twMerge("max-h-[calc(100vh-250px)] border-zinc-300")}>
      <TableInfinite
        columns={columns}
        data={flatData}
        infiniteQuery={{
          data: flatData,
          fetchNextPage,
          isFetching,
          totalCount: data?.pages[0]?.[0]?.count ?? 0,
        }}
        columnSortName={columnSort}
        handleTableSort={handleTableSort}
        gridClasses="grid-cols-[80px_1fr_minmax(200px,_1fr)_minmax(200px,_1fr)_1fr_140px_minmax(110px,_1fr)_minmax(110px,_1fr)_40px]"
        roundedBottom={false}
      />
      <div className="flex items-center justify-end rounded-b-lg border border-t-0 border-zinc-300 bg-zinc-50 p-4">
        <div className="flex items-center gap-2">
          <span className="font-semibold">Total:</span>{" "}
          {formatCurrency(getTotal())}
        </div>
      </div>
    </div>
  );
};
